import { Checkbox, Search, SkeletonText, Layer } from '@carbon/react';
import style from './multi-select-form.module.scss';
import { useCallback, useMemo, useState, ReactNode, useEffect } from 'react';
import { debounce } from 'lodash-es';
import classNames from 'classnames';

// Base interface that all options must extend
export interface Option {
  // Unique identifier for the option
  id: string | number;
  // Display name of the option
  name: string;
}

export interface MultiSelectFormProps<T extends Option> {
  // Array of options to display.
  options: T[];
  // Currently selected options as an array. Pass empty array for no selection.
  value?: T[];
  // Callback function called when selection changes. Receives array of selected options.
  onChange: (value: T[]) => void;
  // Function to render custom label for each option. If not provided, uses default rendering.
  renderLabel?: (option: T) => NonNullable<ReactNode>;
  className?: string;
  onSearch?: (text: string, option: T) => boolean;
  header?: ReactNode;
  isLoading?: boolean;
  id: string;
}

export const MultiSelectForm = <T extends Option>({
  options,
  value = [],
  onChange,
  renderLabel,
  className,
  onSearch,
  header,
  isLoading = false,
  id,
}: MultiSelectFormProps<T>) => {
  const [filteredOptions, setFilteredOptions] = useState<T[]>(options);
  const [searchText, setSearchText] = useState<string>('');

  const [optimisticValue, setOptimisticValue] = useState<T[]>(value);

  useEffect(() => {
    setOptimisticValue(value);
  }, [value]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const isChecked = useCallback(
    (option: T) => {
      return optimisticValue.some((v) => v.id === option.id);
    },
    [optimisticValue]
  );

  const handleChange = (option: T, checked: boolean) => {
    let newValue: T[];

    if (checked) {
      // Add the option to the selection
      newValue = [...optimisticValue, option];
    } else {
      // Remove the option from the selection
      newValue = optimisticValue.filter((v) => v.id !== option.id);
    }

    setOptimisticValue(newValue);

    setTimeout(() => {
      onChange(newValue);
    }, 0);
  };

  // Default label renderer
  const defaultRenderLabel = useCallback((option: T) => {
    return option.name;
  }, []);

  const debouncedFilteredOptions = useMemo(() => {
    if (!onSearch) return;
    return debounce((text: string) => {
      if (!text) return setFilteredOptions(options);

      const filtered = options.filter((option) => onSearch(text, option));

      setFilteredOptions(filtered);
    }, 300);
  }, [options, onSearch]);

  return (
    <div className={classNames(style.container, className)}>
      {debouncedFilteredOptions && (
        <Layer>
          <Search
            value={searchText}
            labelText="Search"
            placeholder="Search input text"
            onChange={(e) => {
              const text = e.target.value;
              setSearchText(text);
              debouncedFilteredOptions(text);
            }}
          />
        </Layer>
      )}
      {header && <div className={style.header}>{header}</div>}
      {isLoading && (
        <div className={style.skeleton}>
          {Array.from({ length: 3 }).map((_, index) => (
            <SkeletonText key={index} />
          ))}
        </div>
      )}
      {!isLoading && filteredOptions.length > 0 && (
        <div className={style.formContainer}>
          {filteredOptions.map((option) => (
            <div
              key={option.id}
              className={style.optionContainer}
              onClick={() => handleChange(option, !isChecked(option))}
            >
              <div className={style.optionLabel}>
                {renderLabel ? renderLabel(option) : defaultRenderLabel(option)}
              </div>
              <div
                className={style.checkboxWrapper}
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  id={`${id}-checkbox-${option.id}`}
                  checked={isChecked(option)}
                  className={style.checkbox}
                  labelText=""
                  aria-label={option.name}
                  onChange={(_evt, { checked }: { checked: boolean }) =>
                    handleChange(option, checked)
                  }
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
